import React, { useCallback, useEffect, useState } from 'react';

import * as Store from '@apply/store';
import { QuestionValidateType, ShowIf, ValidationMessage } from '@apply/ui';
import { NumericFormat } from 'react-number-format';

export interface RentalUnitsProps {
    header?: string;
    units?: Store.PropertiesStore.Types.RentalUnit[];
    propertyGuid?: string;
    submitted?: boolean;
    provideRentalUnitsLater?: boolean;
    includeOccupied?: boolean;
    onQuestionValidate?: QuestionValidateType;
    onChangeProvideRentalUnitsLater?: (guid: string, value: boolean) => void;
    onChangeRentalUnit?: (propertyGuid: string, value: Store.PropertiesStore.Types.RentalUnit) => void;
}

export const safeParseFloat = (input: string, defaultValue): number => {
    try {
        if (input === undefined || input === '') {
            return defaultValue;
        } else {
            const results = parseFloat(input);
            if (isNaN(results)) {
                return defaultValue;
            } else {
                return results;
            }
        }
    } catch {
        /* istanbul ignore next */
        return defaultValue;
    }
};

export const RentalUnits: React.FC<RentalUnitsProps> = ({
    header = 'Unit Information',
    units,
    propertyGuid,
    submitted,
    provideRentalUnitsLater,
    includeOccupied = true,
    onChangeProvideRentalUnitsLater,
    onChangeRentalUnit,
    onQuestionValidate,
}) => {
    const [totalRent, setTotalRent] = useState<number>(0);
    const [unitsFailingValidation, setUnitsFailingValidation] = useState<string[]>([]);

    useEffect(() => {
        const passing = unitsFailingValidation.length === 0 || provideRentalUnitsLater;
        /* istanbul ignore else */
        if (onQuestionValidate) {
            onQuestionValidate('unitinfogroup', passing, 'One or more rental units are missing required information');
        }
    }, [unitsFailingValidation, provideRentalUnitsLater, onQuestionValidate]);

    useEffect(() => {
        if (units?.length) {
            const total = units
                .filter((unit) => unit.rent !== undefined)
                .reduce((subtotal, current) => subtotal + current.rent, 0);
            // istanbul ignore else
            if (total !== undefined && !isNaN(total)) {
                setTotalRent(total);
            } else {
                setTotalRent(0);
            }
        } else {
            setTotalRent(0);
        }
    }, [units]);

    const onChangeUnit = useCallback(
        (newValue: Store.PropertiesStore.Types.RentalUnit) => {
            onChangeRentalUnit && onChangeRentalUnit(propertyGuid, newValue);
            const passing =
                newValue.rent !== undefined &&
                newValue.squareFeet !== undefined &&
                newValue.bathrooms !== undefined &&
                newValue.bedrooms !== undefined &&
                (newValue.occupied !== undefined || !includeOccupied);
            if (passing) {
                setUnitsFailingValidation((prev) => [...prev.filter((itm) => itm !== newValue.guid)]);
            } else {
                setUnitsFailingValidation((prev) => [...prev, newValue.guid]);
            }
        },
        [propertyGuid, includeOccupied, onChangeRentalUnit]
    );

    useEffect(() => {
        // unsets any previously sent validation messages on unmount
        return () => {
            // istanbul ignore else
            if (onQuestionValidate) {
                onQuestionValidate('unitinfogroup', true, undefined);
            }
        };
    }, [onQuestionValidate]);

    const onUnmount = (guid: string) => {
        setUnitsFailingValidation((prev) => [...prev.filter((itm) => itm !== guid)]);
    };

    if (!units?.length) {
        return null;
    }

    return (
        <>
            <div style={{ display: 'flex', alignContent: 'center' }}>
                <div style={{ flex: 3 }}>
                    <h3>{header}</h3>
                </div>
                <div
                    style={{
                        flex: 1,
                        alignContent: 'center',
                        alignSelf: 'center',
                        paddingTop: '20px',
                        paddingRight: '18px',
                        textAlign: 'right',
                    }}
                >
                    <ShowIf showIf={units.length >= 35}>
                        <label htmlFor='rentalUnitsProvideLater'>
                            <input
                                type='checkbox'
                                id='rentalUnitsProvideLater'
                                checked={provideRentalUnitsLater}
                                onChange={() =>
                                    onChangeProvideRentalUnitsLater &&
                                    onChangeProvideRentalUnitsLater(propertyGuid, !provideRentalUnitsLater)
                                }
                            />
                            Provide Later
                        </label>
                    </ShowIf>
                </div>
            </div>
            <ShowIf showIf={!provideRentalUnitsLater}>
                <div className='form-group visual-group'>
                    <table>
                        <thead>
                            <tr>
                                <ShowIf showIf={units?.length > 1}>
                                    <th>Unit</th>
                                </ShowIf>
                                <ShowIf showIf={includeOccupied}>
                                    <th>Occupied</th>
                                </ShowIf>
                                <th>Annual Rent</th>
                                <th>Square Feet</th>
                                <th>Bedrooms</th>
                                <th>Bathrooms</th>
                                <th>Garage Bays</th>
                            </tr>
                        </thead>

                        <tbody>
                            {units.map((unit, index) => {
                                return (
                                    <Unit
                                        unit={unit}
                                        index={index}
                                        key={unit.guid}
                                        onChange={onChangeUnit}
                                        submitted={submitted}
                                        onUnmount={onUnmount}
                                        totalUnitCount={units?.length ?? 0}
                                        includeOccupied={includeOccupied}
                                    />
                                );
                            })}
                        </tbody>

                        <ShowIf showIf={units?.length > 1}>
                            <tfoot>
                                <tr>
                                    <td
                                        className='nowrap primary'
                                        colSpan={includeOccupied ? 2 : 1}
                                    >
                                        Total
                                    </td>
                                    <td className='has-dollar'>
                                        <NumericFormat
                                            value={totalRent}
                                            displayType={'text'}
                                            thousandSeparator={true}
                                            prefix={'$ '}
                                        />
                                    </td>
                                    <td></td>
                                    <td></td>
                                    <td colSpan={2}></td>
                                </tr>
                            </tfoot>
                        </ShowIf>
                    </table>
                </div>
            </ShowIf>
        </>
    );
};

export const isUnitEmpty = (unit: Store.PropertiesStore.Types.RentalUnit): boolean => {
    return areUnitsEqual(unit, {
        ...Store.PropertiesStore.Types.rentalUnitInitialState,
        guid: unit.guid,
    });
};

export const areUnitsEqual = (
    left: Store.PropertiesStore.Types.RentalUnit,
    right: Store.PropertiesStore.Types.RentalUnit
): boolean => {
    if (!left || !right) {
        return false; // always treat undefined objects as not the same thing
    }

    return (
        left.guid === right.guid &&
        left.bathrooms === right.bathrooms &&
        left.bedrooms === right.bedrooms &&
        left.garageBays === right.garageBays &&
        left.occupied === right.occupied &&
        left.rent === right.rent &&
        left.squareFeet === right.squareFeet
    );
};

export interface UnitProps {
    unit: Store.PropertiesStore.Types.RentalUnit;
    index: number;
    submitted?: boolean;
    totalUnitCount?: number;
    includeOccupied?: boolean;
    onChange: (newValue: Store.PropertiesStore.Types.RentalUnit) => void;
    onUnmount: (guid: string) => void;
}

export const Unit: React.FC<UnitProps> = React.memo(
    ({ unit, index, submitted, totalUnitCount, includeOccupied, onChange, onUnmount }) => {
        const [guid] = useState<string>(unit.guid);
        const [rent, setRent] = useState<number>(unit.rent);
        const [squareFeet, setSquareFeet] = useState<number>(unit.squareFeet);
        const [bedrooms, setBedrooms] = useState<number>(unit.bedrooms);
        const [bathrooms, setBathrooms] = useState<number>(unit.bathrooms);
        const [garageBays, setGarageBays] = useState<number>(unit.garageBays);
        const [occupied, setOccupied] = useState<boolean>(unit.occupied);

        useEffect(() => {
            if (isUnitEmpty(unit)) {
                onChange && onChange(unit);
            }
            return () => {
                onUnmount && onUnmount(unit.guid);
            };
        }, []);

        useEffect(() => {
            const newValue = {
                guid: guid,
                occupied,
                rent,
                squareFeet,
                bedrooms,
                bathrooms,
                garageBays,
            };
            if (!areUnitsEqual(newValue, unit)) {
                onChange && onChange(newValue);
            }
        }, [guid, occupied, rent, squareFeet, bedrooms, bathrooms, garageBays, unit, onChange]);

        const handleNumericKeyDown = (e) => {
            if (e.keyCode === 38 || e.keyCode === 40) {
                e.preventDefault();
            }
        };

        return (
            <tr style={{ verticalAlign: 'top' }}>
                <ShowIf showIf={totalUnitCount > 1}>
                    <td className='nowrap primary'>Unit {index + 1}</td>
                </ShowIf>
                <ShowIf showIf={includeOccupied}>
                    <td className='nowrap'>
                        <fieldset>
                            <legend className='sr-only'>Unit {index + 1} Occupied</legend>
                            <label htmlFor={`unit${index}occupiedyes`}>
                                <input
                                    type='radio'
                                    name={`unit${index}occupied`}
                                    id={`unit${index}occupiedyes`}
                                    data-testid={`unit${index}occupiedyes`}
                                    checked={occupied === true}
                                    onChange={() => setOccupied(true)}
                                />
                                Yes
                            </label>
                            <label htmlFor={`unit${index}occupiedno`}>
                                <input
                                    type='radio'
                                    name={`unit${index}occupied`}
                                    id={`unit${index}occupiedno`}
                                    data-testid={`unit${index}occupiedno`}
                                    checked={occupied === false}
                                    onChange={() => setOccupied(false)}
                                />
                                No
                            </label>
                            {occupied === undefined && submitted && <ValidationMessage>Required</ValidationMessage>}
                        </fieldset>
                    </td>
                </ShowIf>
                <td>
                    <span className='has-dollar-unit-info'>
                        <label
                            className='sr-only'
                            htmlFor={`unit${index}rent`}
                        >
                            Unit {index + 1} Annual Rent
                        </label>
                        <NumericFormat
                            aria-labelledby={`unit${index}rent`}
                            data-testid={`unit${index}rent`}
                            value={rent || undefined}
                            placeholder={'$ 0'}
                            decimalScale={2}
                            thousandSeparator={true}
                            prefix={'$ '}
                            onValueChange={(values) => {
                                const { value } = values;
                                setRent(Number(value));
                            }}
                        />
                    </span>
                    {!rent && submitted && <ValidationMessage>Required</ValidationMessage>}
                </td>
                <td>
                    <label
                        className='sr-only'
                        htmlFor={`unit${index}sf`}
                    >
                        Unit {index + 1} Square Feet
                    </label>
                    <NumericFormat
                        aria-labelledby={`unit${index}sf`}
                        data-testid={`unit${index}sf`}
                        value={squareFeet || undefined}
                        thousandSeparator={true}
                        onValueChange={(values) => {
                            const { value } = values;
                            setSquareFeet(Number(value));
                        }}
                    />
                    {!squareFeet && submitted && <ValidationMessage>Required</ValidationMessage>}
                </td>
                <td>
                    <label
                        className='sr-only'
                        htmlFor={`unit${index}bedrooms`}
                    >
                        Unit {index + 1} Bedrooms
                    </label>
                    <input
                        type='number'
                        id={`unit${index}bedrooms`}
                        value={bedrooms?.toString() || ''}
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        onKeyDown={handleNumericKeyDown}
                        min='0'
                        onChange={(e) => {
                            const beds = safeParseFloat(e.target.value, undefined);
                            setBedrooms(beds < 0 ? 0 : beds);
                        }}
                    />
                    {!bedrooms && submitted && <ValidationMessage>Required</ValidationMessage>}
                </td>
                <td>
                    <label
                        className='sr-only'
                        htmlFor={`unit${index}bathrooms`}
                    >
                        Unit {index + 1} Bathrooms
                    </label>
                    <input
                        type='number'
                        id={`unit${index}bathrooms`}
                        value={bathrooms?.toString() || ''}
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        onKeyDown={handleNumericKeyDown}
                        min='0'
                        onChange={(e) => {
                            const baths = safeParseFloat(e.target.value, undefined);
                            setBathrooms(baths < 0 ? 0 : baths);
                        }}
                    />
                    {!bathrooms && submitted && <ValidationMessage>Required</ValidationMessage>}
                </td>
                <td>
                    <label
                        className='sr-only'
                        htmlFor={`unit${index}garage`}
                    >
                        Unit {index + 1} Garage Bays
                    </label>
                    <input
                        type='number'
                        id={`unit${index}garage`}
                        value={garageBays?.toString() || ''}
                        onWheel={(e) => (e.target as HTMLElement).blur()}
                        onKeyDown={handleNumericKeyDown}
                        min='0'
                        onChange={(e) => {
                            const gbays = safeParseFloat(e.target.value, undefined);
                            setGarageBays(gbays < 0 ? 0 : gbays);
                        }}
                    />
                </td>
            </tr>
        );
    }
);
